import { Box, Checkbox } from "@mui/joy";
import { NerdDatePicker, NerdDateTimeRangePicker } from "@nerdjs/nerd-ui";
import { DateTime } from "luxon";
import moment from "moment";
import { useEffect, useState } from "react";
import { useCompanySettings } from "redux/company/companyHooks";

/**
 *
 * @returns {ReactElement} TestDatePicker page
 */
export function DateTimePicker({
  dateFrom,
  dateTo,
  tzName,
  timezoneSelect = false,
  onChange,
  disabled,
}: {
  dateFrom: string;
  dateTo: string;
  timezoneSelect?: boolean;
  tzName: string;
  disabled?: boolean;
  onChange: (dateFrom: string, dateTo: string) => void;
}) {
  // const _tzName = tzName ? tzName : moment.tz.guess();
  const from = moment.tz(dateFrom, tzName).format("HH:mm");
  const to = moment.tz(dateTo, tzName).format("HH:mm");

  const noTimeRange = from === to && to === "00:00";

  const _tzName = tzName ? tzName : moment.tz.guess();
  const settings = useCompanySettings();

  const [noTime, setNoTime] = useState(
    settings.workSessionAllDay && noTimeRange
  );

  useEffect(() => {
    setNoTime(settings.workSessionAllDay && noTimeRange);
  }, [settings, dateFrom, dateTo]);

  return (
    <Box sx={{ opacity: disabled ? 0.3 : 1 }}>
      {noTime && settings.workSessionAllDay ? (
        <NerdDatePicker
          key={`${_tzName}_${disabled}`}
          value={
            dateFrom && !disabled
              ? utcToLocal(DateTime.fromISO(dateFrom), _tzName)
              : null
          }
          onChange={(date) => {
            const _date = date.set({ hour: 0, minute: 0, millisecond: 0 });
            onChange(
              localToUTC(_date, _tzName).toISO(),
              localToUTC(_date, _tzName).toISO()
            );
          }}
        />
      ) : (
        <NerdDateTimeRangePicker
          key={`${_tzName}_${disabled}_${dateFrom}_${dateTo}`}
          defaultTimeZone={"UTC"}
          disableAllDay
          disableTimeZones={!timezoneSelect}
          timeFormat="12"
          startDate={
            dateFrom && !disabled
              ? utcToLocal(DateTime.fromISO(dateFrom), _tzName)
              : null
          }
          endDate={
            dateTo && !disabled
              ? utcToLocal(DateTime.fromISO(dateTo), _tzName)
              : null
          }
          onChange={(startDate, endDate) => {
            if (startDate.hour === 0 && endDate.hour === 0) {
              startDate = startDate.plus({ hours: 8 });
              endDate = endDate.plus({ hours: 9 });
            }

            onChange(
              localToUTC(startDate, _tzName).toISO(),
              localToUTC(endDate, _tzName).toISO()
            );
          }}
        />
      )}
      {settings.workSessionAllDay ? (
        <Checkbox
          checked={noTime}
          onChange={(e) => {
            setNoTime(e.currentTarget.checked);
            let startDate = dateFrom
              ? utcToLocal(DateTime.fromISO(dateFrom), _tzName)
              : DateTime.now();
            let endDate = dateTo
              ? utcToLocal(DateTime.fromISO(dateTo), _tzName)
              : DateTime.now();

            if (e.currentTarget.checked) {
              startDate = startDate.set({ hour: 0, minute: 0 });
              endDate = endDate.set({ hour: 0, minute: 0 });
              onChange(
                localToUTC(startDate, _tzName).toISO(),
                localToUTC(endDate, _tzName).toISO()
              );
            } else {
              startDate = startDate.set({ hour: 8, minute: 0 });
              endDate = endDate.set({ hour: 9, minute: 0 });
              onChange(
                localToUTC(startDate, _tzName).toISO(),
                localToUTC(endDate, _tzName).toISO()
              );
            }
          }}
          sx={{ pl: 5 }}
          size="sm"
          label="No Time"
        />
      ) : (
        []
      )}
    </Box>
  );
}

function localToUTC(date: DateTime, tzName: string): DateTime {
  return date.setZone(tzName, { keepLocalTime: true }).toUTC();
}

function utcToLocal(date: DateTime, tzName: string): DateTime {
  return date.setZone(tzName).setZone("UTC", { keepLocalTime: true });
}
